import React from "react";
import { Image, MotiView } from "moti";
import { View, Text, Pressable, StyleSheet } from "react-native";
import CustomProgressBar from "../../common/general/CustomProgressBar/CustomProgressBar";
import CheckedBadge from "../../svgs/common/CheckedBadge";
import { PlusCircle } from "../../svgIcons";
import CustomButton from "../../common/general/CustomButton/CustomButton";
import { shadowStyles } from "../../../styles/shadow";
import Edit from "../../svgs/common/Edit";
import { ChartLine } from "../../svgs/common";
import { useAppConfig } from "../../../AppConfigProvider";
import CustomSpacing from "../../common/layout/CustomSpacing/CustomSpacing";
import RoundedLabel from "../../common/label/RoundedLabel/RoundedLabel";
import CustomText from "../../common/general/CustomText/CustomText";
import { formatDate } from "../../../utils/date";
import useResponsiveScreen from "../../../hooks/useResponsiveScreen";
import { CourseGenerationStatus } from "../../../graphql/generated/graphql";

function ClassCardProgress({
  title,
  onPress,
  type = "core",
  isDraft = false,
  progress = 100,
  hasEdit = false,
  hasAnalytics = false,
  onEditPress = undefined,
  onAnalyticsPress = undefined,
  thumbnail,
  isPlaceholder = false,
  generationStatus,
  startDate,
  disabled = false,
}) {
  const { primaryColor } = useAppConfig();
  const { isMedium } = useResponsiveScreen();

  const getStatusText = (progress) => {
    if (typeof progress === "number") {
      if (progress === 0) {
        return "Not Started";
      } else if (progress > 0 && progress < 100) {
        return "In Progress";
      } else if (progress >= 100) {
        return "Complete";
      }
    }
    return "Not Started";
  };

  const statusText = getStatusText(progress);
  const isComplete = progress >= 100;

  const isGenerating =
    generationStatus &&
    generationStatus !== CourseGenerationStatus.Completed &&
    generationStatus !== CourseGenerationStatus.GeneratingImageCompleted &&
    generationStatus !== CourseGenerationStatus.GeneratingImageFailed &&
    generationStatus !== CourseGenerationStatus.GeneratingImage;
  const isGenerationCompleted =
    generationStatus === CourseGenerationStatus.Completed ||
    generationStatus === CourseGenerationStatus.GeneratingImageCompleted ||
    generationStatus === CourseGenerationStatus.GeneratingImageCompleted ||
    generationStatus === CourseGenerationStatus.GeneratingImage ||
    !generationStatus;
  const isGenerationFailed = generationStatus === CourseGenerationStatus.Failed;
  const hasStartDateInTheFuture = startDate && new Date(startDate) > new Date();

  if (isPlaceholder) {
    return (
      <MotiView
        from={{
          opacity: 0,
          translateY: 20,
        }}
        animate={{
          opacity: 1,
          translateY: 0,
        }}
        // @ts-expect-error - transition is not typed
        transition={{ type: "timing", duration: 500 }}>
        <Pressable
          onPress={onPress}
          disabled={disabled}
          style={({ hovered }) => [styles.axioCard, hovered && styles.axioCardHovered]}>
          <View style={styles.placeholderContent}>
            <CustomButton
              text={" " + type.charAt(0).toUpperCase() + type.slice(1)}
              styleType="primaryLight"
              onPress={null}
              leftIcon={<PlusCircle />}
              aria-label="Add a Course"
              style={shadowStyles.allAroundShadow}
            />
          </View>
          {disabled && <View style={styles.overlay} />}
        </Pressable>
      </MotiView>
    );
  }

  return (
    <MotiView
      from={{
        opacity: 0,
        translateY: 20,
      }}
      animate={{
        opacity: 1,
        translateY: 0,
      }}
      delay={1000}
      // @ts-expect-error - transition is not typed
      transition={{ type: "timing", duration: 500 }}
      aria-label={`Class card ${title}`}
      style={[styles.container, isMedium && { minWidth: 400 }]}>
      <Pressable
        onPress={onPress}
        disabled={disabled || isGenerating}
        style={({ hovered }) => [styles.axioCard, hovered && styles.axioCardHovered]}>
        <View style={styles.axioContainer}>
          <View style={styles.headerSection}>
            <View style={styles.thumbnailContainer}>
              {thumbnail ? (
                <Image source={{ uri: thumbnail }} style={styles.thumbnail} />
              ) : (
                <View style={styles.emptyThumbnailSpace} />
              )}
              <View style={styles.overlayContent}>
                <View style={styles.headerRow}>
                  <View style={styles.buttonRow}>
                    {isDraft && (
                      <>
                        <CustomSpacing type="horizontal" size="s" />
                        <RoundedLabel
                          text="Draft"
                          textStyle={{ fontSize: 14 }}
                          style={styles.draftLabel}
                          padding={[8, 6]}
                          isRounded={true}
                        />
                      </>
                    )}
                  </View>
                  <View style={styles.buttonRow}>
                    {hasEdit && (
                      <CustomButton
                        leftIcon={<Edit fill={primaryColor} />}
                        style={shadowStyles.allAroundShadow}
                        onPress={onEditPress}
                        disabled={isGenerating}
                        accessibilityLabel="Edit course"
                      />
                    )}
                    {hasAnalytics && (
                      <CustomButton
                        leftIcon={<ChartLine fill={primaryColor} />}
                        style={shadowStyles.allAroundShadow}
                        onPress={onAnalyticsPress}
                        disabled={isGenerating}
                        accessibilityLabel="View analytics"
                      />
                    )}
                  </View>
                </View>
              </View>
            </View>
          </View>
          <View style={styles.titleContainer}>
            <Text style={styles.axioTitle} numberOfLines={2} ellipsizeMode="tail">
              {title}
            </Text>
            {hasStartDateInTheFuture && (
              <View style={styles.dateRow}>
                <CustomText text="Start Date with colon" size="s" weight="medium" style={{ color: "#667085" }} />
                <CustomText
                  text={formatDate(startDate, false)}
                  useTranslationText={false}
                  size="s"
                  weight="ultraBold"
                />
              </View>
            )}
          </View>
          <View style={styles.progressContainer}>
            {isGenerationCompleted && (
              <>
                <View style={styles.progressStatus}>
                  <View style={{ flexDirection: "row", alignItems: "center", gap: 5 }}>
                    {isComplete && <CheckedBadge />}
                    <Text style={styles.statusText}>{statusText}</Text>
                  </View>
                </View>
                <CustomProgressBar ariaLabel="Course progress bar" progress={progress} color={primaryColor} />
              </>
            )}
            {isGenerating && (
              <View style={styles.progressStatus}>
                <CustomText
                  style={styles.statusText}
                  text="We are currently processing the Course. Please check back later."
                />
              </View>
            )}
            {isGenerationFailed && (
              <View style={styles.progressStatus}>
                <CustomText style={styles.statusText} text="Failed to generate the course. Please try again." />
              </View>
            )}
          </View>
        </View>
        {disabled && <View style={styles.overlay} />}
      </Pressable>
    </MotiView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    maxWidth: 570,
  },
  axioCard: {
    backgroundColor: "#FFFFFF",
    borderRadius: 24,
    marginVertical: 8,
    marginHorizontal: 16,
    flexGrow: 1,
    borderWidth: 1,
    borderColor: "#EAECF0",
    outlineWidth: 0, // Prevent default outline flash
    outlineColor: "transparent", // Explicitly set to transparent
    outlineStyle: "solid", // Required for consistency
    transition: "all 200ms cubic-bezier(0.4, 0, 0.2, 1)", // Smooth transition
  },
  axioCardHovered: {
    outlineWidth: 3, // Simulate a thicker border
    outlineColor: "#D0D5DD", // Desired hover color
    outlineStyle: "solid",
    boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)", // Optional shadow for hover effect
    cursor: "pointer",
  },
  axioContainer: {
    gap: 12,
  },
  axioTitle: {
    fontSize: 16,
    fontWeight: "600",
    color: "#101828",
    lineHeight: 24,
    fontFamily: "Inter",
  },
  titleContainer: {
    paddingLeft: 20,
    display: "flex",
    flexDirection: "row",
    gap: 8,
    justifyContent: "space-between",
  },
  headerSection: {
    position: "relative",
  },
  thumbnailContainer: {
    position: "relative",
    borderTopLeftRadius: 24,
    borderTopRightRadius: 24,
    overflow: "hidden",
  },
  emptyThumbnailSpace: {
    width: "100%",
    height: 200,
    backgroundColor: "#FFFFFF",
  },
  placeholderContent: {
    alignItems: "center",
    justifyContent: "center",
    gap: 12,
  },
  progressContainer: {
    padding: 20,
    paddingBottom: 24,
    borderTopWidth: 1,
    borderTopColor: "#EAECF0",
    gap: 12,
  },
  progressStatus: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  statusText: {
    fontSize: 14,
    fontWeight: "500",
    color: "#667085",
  },
  thumbnail: {
    width: "100%",
    height: 200,
    borderTopLeftRadius: 24,
    borderTopRightRadius: 24,
  },
  overlayContent: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    padding: 24,
  },
  headerRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  buttonRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  draftLabel: {
    backgroundColor: "#EAECF0",
    borderRadius: 360,
  },
  dateRow: {
    flexDirection: "row",
    justifyContent: "flex-start",
    alignItems: "center",
  },
  overlay: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(255, 255, 255, 0.8)",
    justifyContent: "center",
    alignItems: "center",
    borderRadius: 24,
  },
});

export default ClassCardProgress;
